/**
 * Telemetry events for the Global Experience Launcher.
 * All events are structured and privacy-aware (no raw query text in prod).
 */

export type LauncherOpenSource = 'keyboard' | 'header_button' | 'mega_menu';

export type LauncherEvent =
  | { name: 'global_launcher_opened'; props: { source: LauncherOpenSource; currentRoute: string; currentPluginId?: string } }
  | { name: 'global_launcher_query_changed'; props: { normalizedQueryLength: number } }
  | { name: 'global_launcher_result_selected'; props: { selectedResultId: string; selectedResultRank: number; queryLength: number; source: LauncherOpenSource } }
  | { name: 'global_launcher_dismissed'; props: { queryLength: number; hadResults: boolean; durationMs: number } }
  | { name: 'global_launcher_zero_results'; props: { normalizedQueryLength: number } }
  | { name: 'global_launcher_pin_added'; props: { pluginId: string } }
  | { name: 'global_launcher_pin_removed'; props: { pluginId: string } }
  | { name: 'global_launcher_recent_selected'; props: { pluginId: string } }
  | { name: 'global_launcher_recent_removed'; props: { pluginId: string } }
  | { name: 'global_launcher_recents_cleared'; props: { count: number } }
  | { name: 'mega_menu_outside_result_selected'; props: { pluginId: string; originalMenuGroup: string; queryLength: number } };

interface TelemetryClient {
  track: (name: string, props: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    __EMPOWERNOW_TELEMETRY__?: TelemetryClient;
  }
}

let launcherOpenTimestamp = 0;
let lastOpenSource: LauncherOpenSource = 'keyboard';

function emit(event: LauncherEvent): void {
  try {
    if (typeof window !== 'undefined' && window.__EMPOWERNOW_TELEMETRY__) {
      window.__EMPOWERNOW_TELEMETRY__.track(event.name, event.props as Record<string, unknown>);
    } else if (import.meta.env.DEV) {
      console.debug('[Launcher Telemetry]', event.name, event.props);
    }
  } catch {
    // Never let telemetry crash the app
  }
}

export function trackLauncherOpened(source: LauncherOpenSource, currentRoute: string, currentPluginId?: string): void {
  launcherOpenTimestamp = Date.now();
  lastOpenSource = source;
  emit({
    name: 'global_launcher_opened',
    props: { source, currentRoute, currentPluginId },
  });
}

export function trackLauncherQueryChanged(queryLength: number): void {
  emit({
    name: 'global_launcher_query_changed',
    props: { normalizedQueryLength: queryLength },
  });
}

export function trackLauncherResultSelected(selectedResultId: string, selectedResultRank: number, queryLength: number): void {
  emit({
    name: 'global_launcher_result_selected',
    props: { selectedResultId, selectedResultRank, queryLength, source: lastOpenSource },
  });
}

export function trackLauncherDismissed(queryLength: number, hadResults: boolean): void {
  const durationMs = launcherOpenTimestamp > 0 ? Date.now() - launcherOpenTimestamp : 0;
  emit({
    name: 'global_launcher_dismissed',
    props: { queryLength, hadResults, durationMs },
  });
}

export function trackLauncherZeroResults(queryLength: number): void {
  emit({
    name: 'global_launcher_zero_results',
    props: { normalizedQueryLength: queryLength },
  });
}

export function trackLauncherPinAdded(pluginId: string): void {
  emit({ name: 'global_launcher_pin_added', props: { pluginId } });
}

export function trackLauncherPinRemoved(pluginId: string): void {
  emit({ name: 'global_launcher_pin_removed', props: { pluginId } });
}

export function trackLauncherRecentSelected(pluginId: string): void {
  emit({ name: 'global_launcher_recent_selected', props: { pluginId } });
}

export function trackLauncherRecentRemoved(pluginId: string): void {
  emit({ name: 'global_launcher_recent_removed', props: { pluginId } });
}

export function trackLauncherRecentsCleared(count: number): void {
  emit({ name: 'global_launcher_recents_cleared', props: { count } });
}

export function trackMegaMenuOutsideResult(pluginId: string, originalMenuGroup: string, queryLength: number): void {
  emit({
    name: 'mega_menu_outside_result_selected',
    props: { pluginId, originalMenuGroup, queryLength },
  });
}
